import { ServiceList } from '@bimser/common';
import { FormItemTypes, IDictionary, isNullOrUndefined } from '@bimser/core';
import { JSX } from 'react';
import { IPropertyField, ISize, MultiLanguageText } from '../../../../entities/common';
import VisualControl from '../../base/VisualControl';
import Styles from './assets/imageStyles.scss';
import { IImageProperties } from './entities/IImageProperties';
import ImageSourceTypes from './entities/ImageSourceTypes';

export default class Image extends VisualControl {
	fileName: string;
	alt: MultiLanguageText;
	size: ISize;
	sourceType: ImageSourceTypes;
	fileId: string;
	imageBase64: string;
	isImageLoaded: boolean;
	isImageLoading: boolean;

	constructor() {
		super();
		this.controlType = FormItemTypes.EControlTypes.Image;

		this.size = { height: 200, width: 200 };
		this.sourceType = ImageSourceTypes.local;
		this.imageBase64 = null;
		this.isImageLoaded = false;
		this.isImageLoading = false;
	}

	__getMLClassPath() {
		return [...super.__getMLClassPath(), 'alt'];
	}

	initialize() {
		super.initialize();

		this.alt = new MultiLanguageText(
			(text: string, culture: string, existCultures: string[]) => {
				this.__onChangeMLTextValue(text, 'alt', culture, existCultures);
			}
		);
	}

	__loadProperties(data: IImageProperties) {
		super.__loadProperties(data);

		if (data.alt) {
			this.alt.setData(data.alt);
		}
		this.__proxyChanged.fileId = data.fileId;
		this.__proxyChanged.fileName = data.fileName;
		this.__proxyChanged.size = data.size;
		this.__proxyChanged.sourceType = data.sourceType;
		this.__proxyChanged.imageBase64 = data.imageBase64;

		if (
			this.fileId &&
			this.__form &&
			this.__form.displayMode == 'preview' &&
			!this.isImageLoading &&
			!this.imageBase64
		) {
			this.isImageLoading = true;
			this.__proxyChanging.isImageLoaded = false;
			this.GetFileDownloadURL();
		}
	}

	GetFileDownloadURL() {
		this.getFile({
			itemId: this.fileId,
			itemType: 1,
			passKey: this.__form.passKey
		})
			.then((base64: string) => {
				this.imageBase64 = base64;
				this.__proxyChanging.isImageLoaded = true;
				this.isImageLoading = false;
			})
			.catch(reason => {
				console.log(reason);
			});
	}

	getFile(args: {
		itemId: string;
		itemType: number;
		projectSecretKey?: string;
		passKey?: string;
	}) {
		return new Promise<string>((resolve, reject) => {
			const params = {
				itemId: args.itemId,
				itemType: args.itemType,
				projectId: this.__form.projectName,
				projectSecretKey: args.projectSecretKey,
				passKey: args.passKey
			};

			ServiceList.SolutionExplorer.SolutionExplorer.GetItemContent.callPromise(params)
				.then((response: any) => {
					if (response && response.itemData) {
						resolve(response.itemData);
					} else {
						reject('No itemData found');
					}
				})
				.catch(err => {
					reject(err);
				});
		});
	}

	__getPropertyFields(): IPropertyField[] {
		const fields = super.__getPropertyFields();
		return [
			...fields,
			{
				name: 'fileName',
				type: 'string',
				common: true
			},
			{
				name: 'size',
				type: 'object',
				items: [
					{
						name: 'width',
						type: 'number'
					},
					{
						name: 'height',
						type: 'number'
					}
				]
			}
		];
	}

	__getSrcUrl() {
		if (this.imageBase64) {
			return `data:image/jpeg;base64,${this.imageBase64}`;
		} else {
			return this.fileName &&
				(this.fileName.substring(0, 7) == 'http://' ||
					this.fileName.substring(0, 8) == 'https://')
				? this.fileName
				: this.__form && !isNullOrUndefined(this.__form.serviceUrl)
				? this.__form.serviceUrl.replace('/api', '/asset/' + this.fileName)
				: '';
		}
	}

	__getComponentData(): IDictionary<any> {
		return {
			...super.__getComponentData(),
			fileName: this.fileName,
			alt: this.alt.getData(),
			size: this.size,
			sourceType: this.sourceType,
			fileId: this.fileId,
			imageBase64: this.imageBase64,
			isImageLoaded: this.isImageLoaded,
			isImageLoading: this.isImageLoading,
			src: this.__getSrcUrl()
		};
	}

	__render(children?: JSX.Element) {
		const src: string = this.__getSrcUrl();

		return super.__render(
			<img
				src={src}
				alt={this.alt.getText()}
				style={this.style}
				className={Styles.formImage}
				{...this.size}
			/>
		);
	}
}
